import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faClose, faCloudArrowUp } from "@fortawesome/free-solid-svg-icons";
import { Col, Input, Label } from "reactstrap";
import React, { useEffect, useState, useRef } from "react";
import ReactCrop, { Crop } from "react-image-crop";
import { Modal, ModalFooter, Row } from "react-bootstrap";
import "react-image-crop/dist/ReactCrop.css";
import './index.css';
import CancelButtonComponents from "../CancelButtonComponent";
import ButtonComponentV2 from "../ButtonComponentV2";
import { toast } from "react-toastify";

interface ImageUploadProps {
  imageUrl: string;
  onUpload: (data: {
    imgSrc: string;
    formData: File | null;
    fileExtension: string | undefined;
  }) => void;
  imgSizeLogic?: boolean;
  imgRemoveLogic?: boolean;
}

const EditPhotoUpload: React.FC<ImageUploadProps> = ({
  onUpload,
  imgSizeLogic,
}) => {
  // state for select image
  const [selectedImage, setSelectedImage] = useState<{
    file: File | null;
    name: string | null;
  }>({ file: null, name: null });

  // state for cropped image
  const [croppedImage, setCroppedImage] = useState<{
    file: File | null;
    name: string | null;
  }>({ file: null, name: null });

  //state for cropping image
  const [crop, setCrop] = useState<Crop>({
    unit: "%",
    x: 0,
    y: 0,
    width: 100,
    height: 100,
  });

  // state for show moadle
  const [show, setShow] = useState(false);

  // state for image size text
   // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [isImageSizeBetter, setIsImageSizeBetter] = useState(false);

   // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [error, setError] = useState<string | null>(null);

  const imgRef = useRef<HTMLImageElement | null>(null);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleToggle = () => {
    setShow(!show);
  };

  const handleClose = () => {
    handleToggle();
  };

  useEffect(() => {
    if (selectedImage?.file !== null) {
      handleToggle();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedImage]);

  useEffect(() => {
    if (
      crop?.width > 300 &&
      crop?.width < 350 &&
      crop?.height > 200 &&
      crop?.height < 250
    ) {
      setIsImageSizeBetter(true);
    } else {
      setIsImageSizeBetter(false);
    }
  }, [crop]);

  const handleSetCroppedImage = () => {
    if (!imgRef.current || !crop.width || !crop.height) {
      return;
    }

    const canvas = document.createElement("canvas");
    const scaleX = imgRef.current.naturalWidth / imgRef.current.width;
    const scaleY = imgRef.current.naturalHeight / imgRef.current.height;
    canvas.width = crop.width * scaleX;
    canvas.height = crop.height * scaleY;
    const ctx = canvas.getContext("2d");

    if (ctx) {
      ctx.drawImage(
        imgRef.current,
        crop.x * scaleX,
        crop.y * scaleY,
        crop.width * scaleX,
        crop.height * scaleY,
        0,
        0,
        crop.width * scaleX,
        crop.height * scaleY
      );

      canvas.toBlob((blob) => {
        if (blob) {
          const file = new File([blob], selectedImage.name ?? "cropped.jpg", {
            type: blob.type,
          });
          const fileUrl = URL.createObjectURL(file);
          setCroppedImage({ file, name: selectedImage.name });
          onUpload({
            imgSrc: fileUrl,
            formData: file,
            fileExtension: selectedImage.name?.split(".").pop(),
          });
          handleToggle();
        }
      }, "image/jpeg");
    }
  };

  /**
   * local image set function
   */
  const handleImage = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      const fileExtension = file.name.split(".").pop()?.toLowerCase();

      // Allow only PNG, JPG, and JPEG files
      if (fileExtension !== "png" && fileExtension !== "jpg" && fileExtension !== "jpeg") {
        toast.warning('Only PNG, JPG, and JPEG files are allowed.')
        setError("Only PNG, JPG, and JPEG files are allowed.");
        return;
      }

      if (!imgSizeLogic) {
        if (file.size > 10 * 1024 * 1024) {
          setError("File size must be below 10MB.");
          return;
        }
      }

      const reader = new FileReader();
      reader.onload = (event) => {
        const img = new Image();
        img.onload = () => {
          if (imgSizeLogic) {
            setSelectedImage({ file, name: file.name });
            onUpload({
              imgSrc: URL.createObjectURL(file),
              formData: file,
              fileExtension,
            });
          } else {
            setError(null);
            setSelectedImage({ file, name: file.name });
            onUpload({
              imgSrc: URL.createObjectURL(file),
              formData: file,
              fileExtension,
            });
          }

          // Set the initial crop to match the image dimensions
          setCrop({
            unit: "%",
            x: 0,
            y: 0,
            width: 60,
            height: 40,
          });
        };
        img.src = event.target?.result as string;
      };
      reader.readAsDataURL(file);
    }
  };

  /**
   * image remove
   */
  const handleRemoveImage = () => {
    setSelectedImage({ file: null, name: null });
    setCroppedImage({ file: null, name: null });
    onUpload({ imgSrc: "", formData: null, fileExtension: "" });
    setError(null);
  };

  return (
    <>
      <Row>
        <Col>
          <div>
            {croppedImage.file ? (
              <div className="edit-photo-selected-img-whole-component">
                <img
                  src={URL.createObjectURL(croppedImage.file)}
                  style={{ width: "60%" }}
                  alt="Cropped"
                />
                {!imgSizeLogic ? (
                  <p className="edit-photo-selected-img-name">
                    {croppedImage.name}
                  </p>
                ) : (
                  <p></p>
                )}
                <FontAwesomeIcon
                  icon={faClose}
                  onClick={handleRemoveImage}
                  className="edit-photo-selected-img-close-icon"
                />
              </div>
            ) : (
              <div>
                {!imgSizeLogic && (
                  <>
                    <div className="d-flex justify-content-center flex-column gap-2 align-items-center text-center">
                      <Label
                        htmlFor="fileInput"
                        className="edit-photo-non-selected-img-label"
                      >
                        <div className="d-flex align-items-center gap-2">
                        <FontAwesomeIcon icon={faCloudArrowUp} size="2xl" />
                        <span className="text-danger">Click to upload</span>
                        </div>
                        PNG, JPG, or JPEG (up to 10MB), 320-1,144 pixels wide
                      </Label>
                      <Input
                        type="file"
                        accept="image/*"
                        className="edit-photo-non-selected-img-content-input"
                        id="fileInput"
                        onChange={handleImage}
                      />
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        </Col>
      </Row>

      <Modal show={show} onHide={handleClose} backdrop="static">
        <Modal.Header closeButton>Crop Image</Modal.Header>
        <Modal.Body>
          <div className="d-flex align-items-center justify-content-center popup-window-image-fit">
            {selectedImage.file && (
              <ReactCrop
                crop={crop}
                onChange={(newCrop: Crop) => setCrop(newCrop)}
                onComplete={(c: Crop) => setCrop(c)}
                aspect={16 / 9}
              >
                <img
                  ref={imgRef}
                  src={URL.createObjectURL(selectedImage.file)}
                  alt="Crop"
                  onLoad={() => {
                    if (imgRef.current) {
                      imgRef.current.width = imgRef.current.naturalWidth;
                      imgRef.current.height = imgRef.current.naturalHeight;
                    }
                  }}
                />
              </ReactCrop>
            )}
          </div>
        </Modal.Body>
        <ModalFooter>
          <CancelButtonComponents onClick={handleClose} titel={"Cancel"} />
          <ButtonComponentV2
            onClick={handleSetCroppedImage}
            titel={"Set Image"}
            color="#fff"
            backgroundColor="#590B21"
          />
        </ModalFooter>
      </Modal>
    </>
  );
};

export default EditPhotoUpload;
